import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { InfoTable, Labelbox, Button, ClientBusinessManager } from "../../components";
import PhoneInput from "../../components/PhoneInput";
import { Grid } from "@material-ui/core";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from "@material-ui/icons";
import "./profile.scss";
import {
  getClientById,
  insertUpdateClient,
} from "../../redux/action/clientAction";
import { MASTER_ACTION, EMPLOYEE_ACTION } from "../../redux/action";
import { toast } from "react-toastify";
import { useClientCategoryTab } from "../../hooks/useClientCategoryTab";
import { clientHasBusinessAccess } from "../../utils/clientBusinessAccess";
import { renderMeetingLink } from "../../utils/meetingLink";

const Profile = () => {
  const dispatch = useDispatch();
  const userId = localStorage.getItem("user_id");
  const USER_DATA = JSON.parse(localStorage.getItem("USER_DATA") || "{}");
  const [profileData, setProfileData] = useState(USER_DATA || {});
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [saving, setSaving] = useState(false);

  const userRole = (localStorage.getItem("user_role") || "").toLowerCase();
  const isEmployeeOrManager = userRole === "employee" || userRole === "manager";
  const [employeeData, setEmployeeData] = useState(null);
  const [managerData, setManagerData] = useState(null);
  const [loadingEmployee, setLoadingEmployee] = useState(false);

  const normalizeGetEmployeePayload = (payload) => {
    if (payload == null) return {};
    if (Array.isArray(payload)) {
      const first = payload[0];
      return first && typeof first === "object" ? first : {};
    }
    if (typeof payload === "object") return payload;
    return {};
  };

  const {
    categoryTab,
    showCategoryChooser,
    syncBusinessAccess,
  } = useClientCategoryTab();

  const { master } = useSelector((state) => state);

  // Fetch master data (countries) on mount
  useEffect(() => {
    dispatch(MASTER_ACTION.getMasterData({ name: "country" }));
  }, [dispatch]);

  // Fetch states when country_id is available (after profile data loads)
  useEffect(() => {
    if (profileData.country_id) {
      dispatch(
        MASTER_ACTION.getMasterData({
          name: "state",
          id: profileData.country_id,
        })
      );
    }
  }, [dispatch, profileData.country_id]);

  // Fetch business states when business_country_id is available
  useEffect(() => {
    if (profileData.business_country_id) {
      dispatch(
        MASTER_ACTION.getMasterData({
          name: "state",
          name_type: "business_state",
          id: profileData.business_country_id,
        })
      );
    }
  }, [dispatch, profileData.business_country_id]);

  useEffect(() => {
    if (!isEmployeeOrManager && userId) {
      dispatch(getClientById(userId))
        .unwrap()
        .then((data) => {
          if (data) {
            setProfileData(data);
            localStorage.setItem("USER_DATA", JSON.stringify(data));
            syncBusinessAccess(data);
          }
        })
        .catch(() => {});
    }
  }, [dispatch, userId, isEmployeeOrManager]);

  useEffect(() => {
    if (isEmployeeOrManager && userId) {
      setLoadingEmployee(true);
      dispatch(EMPLOYEE_ACTION.getEmployee(userId))
        .unwrap()
        .then(async (data) => {
          const detail = normalizeGetEmployeePayload(data);
          setEmployeeData(detail);
          if (detail && detail.manager_id) {
            try {
              const mData = await dispatch(
                EMPLOYEE_ACTION.getEmployee(String(detail.manager_id))
              ).unwrap();
              setManagerData(normalizeGetEmployeePayload(mData));
            } catch (err) {
              console.error("Failed to load manager details:", err);
            }
          }
        })
        .catch((err) => {
          console.error("Failed to load employee details:", err);
        })
        .finally(() => {
          setLoadingEmployee(false);
        });
    }
  }, [dispatch, userId, isEmployeeOrManager]);

  // Helper function to get country name from ID
  const getCountryName = (countryId) => {
    if (
      !countryId ||
      !master.country ||
      !Array.isArray(master.country) ||
      master.country.length === 0
    ) {
      return "";
    }
    // Try to find country by ID - handle different possible field names and types
    const country = master.country.find((c) => {
      const cId = c.country_id || c.id;
      return (
        Number(cId) === Number(countryId) || String(cId) === String(countryId)
      );
    });
    // Try different possible field names for the country name (value is most common for dropdowns)
    return country
      ? country.value ||
          country.country_name ||
          country.name ||
          country.country ||
          ""
      : "";
  };

  // Helper function to get state name from ID
  const getStateName = (stateId, isBusiness = false) => {
    if (!stateId) return "";
    const stateList = isBusiness ? master.business_state : master.state;
    if (!stateList || !Array.isArray(stateList) || stateList.length === 0) {
      return "";
    }
    // Try to find state by ID - handle different possible field names and types
    const state = stateList.find((s) => {
      const sId = s.state_id || s.id;
      return Number(sId) === Number(stateId) || String(sId) === String(stateId);
    });
    // Try different possible field names for the state name (value is most common for dropdowns)
    return state
      ? state.value || state.state_name || state.name || state.state || ""
      : "";
  };

  const handleEditClick = () => {
    setEditedData({
      first_name: profileData.first_name || "",
      last_name: profileData.last_name || "",
      email: profileData.email || USER_DATA.email || "",
      phone: profileData.phone || "",
      address_1: profileData.address_1 || "",
      address_2: profileData.address_2 || "",
      country_id: profileData.country_id || "",
      state_id: profileData.state_id || "",
      city_id: profileData.city_id || "",
      zipcode: profileData.zipcode || "",
      business_name: profileData.business_name || "",
      business_type: profileData.business_type || "",
      business_phone: profileData.business_phone || "",
      business_address_1: profileData.business_address_1 || "",
      business_address_2: profileData.business_address_2 || "",
      business_country_id: profileData.business_country_id || null,
      business_state_id: profileData.business_state_id || null,
      business_city_id: profileData.business_city_id || null,
      business_zipcode: profileData.business_zipcode || null,
    });
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedData({});
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const response = await insertUpdateClient({
        params: editedData,
        insertUpdateFlag: 1,
        client_id: userId,
      });

      // Check for successful response (status 200 or data exists)
      if (response?.data) {
        toast.success("Profile updated successfully!");
        // Refresh profile data
        dispatch(getClientById(userId))
          .unwrap()
          .then((data) => {
            if (data) {
              setProfileData(data);
              localStorage.setItem("USER_DATA", JSON.stringify(data));
              syncBusinessAccess(data);
            }
          });
        setIsEditMode(false);
        setEditedData({});
      } else {
        toast.error(response?.data?.message || "Failed to update profile");
      }
    } catch (error) {
      toast.error("An error occurred while updating profile");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // When country changes, fetch states for that country
    if (field === "country_id") {
      dispatch(
        MASTER_ACTION.getMasterData({
          name: "state",
          id: value,
        })
      );
    }

    // When business country changes, fetch states for that country
    if (field === "business_country_id") {
      dispatch(
        MASTER_ACTION.getMasterData({
          name: "state",
          name_type: "business_state",
          id: value,
        })
      );
    }
  };

  // Check if client type is business
  const isBusinessClient = clientHasBusinessAccess(profileData);

  // Render editable field using Labelbox component
  const renderEditableField = (field, value, config = {}) => {
    if (!isEditMode) {
      return value || "-";
    }

    const {
      type = "text",
      dropdown = [],
      disabled = false,
      labelname = "",
    } = config;

    if (type === "phone") {
      const currentValue =
        field in editedData ? editedData[field] : value ?? "";
      const normalizedValue = currentValue || "+1"; // ensure code is visible by default

      return (
        <div className="profile-edit-field">
          <PhoneInput
            labelname={labelname || undefined}
            value={normalizedValue}
            onChange={(val) => {
              const phoneValue =
                val && !val.startsWith("+") ? `+${val}` : val || "";
              handleFieldChange(field, phoneValue);
            }}
            disabled={disabled}
            placeholder=""
          />
        </div>
      );
    }

    return (
      <div className="profile-edit-field">
        <Labelbox
          type={type}
          labelname={labelname}
          value={field in editedData ? editedData[field] : value ?? ""}
          changeData={(val) => handleFieldChange(field, val)}
          dropdown={dropdown}
          disabled={disabled}
        />
      </div>
    );
  };

  const profileDetails = useMemo(() => {
    const personalInfo = isEditMode
      ? {
          name: (
            <div style={{ display: "flex", gap: "8px" }}>
              <Labelbox
                type="text"
                labelname="First Name"
                value={editedData.first_name || ""}
                changeData={(val) => handleFieldChange("first_name", val)}
              />
              <Labelbox
                type="text"
                labelname="Last Name"
                value={editedData.last_name || ""}
                changeData={(val) => handleFieldChange("last_name", val)}
              />
            </div>
          ),
          email: (
            <div className="profile-readonly-field">
              {editedData.email || profileData.email || USER_DATA.email || "-"}
            </div>
          ),
          phone: renderEditableField("phone", profileData.phone, {
            type: "phone",
            labelname: "",
          }),
          address1: renderEditableField("address_1", profileData.address_1),
          address2: renderEditableField("address_2", profileData.address_2),
          country: renderEditableField(
            "country_id",
            getCountryName(profileData.country_id),
            { type: "select", dropdown: master.country || [] }
          ),
          state: renderEditableField(
            "state_id",
            getStateName(profileData.state_id),
            { type: "select", dropdown: master.state || [] }
          ),
          city: renderEditableField("city_id", profileData.city_id),
          zip: renderEditableField("zipcode", profileData.zipcode),
        }
      : {
          name: `${profileData.first_name || ""} ${
            profileData.last_name || ""
          }`,
          email: profileData.email || USER_DATA.email || "-",
          phone: profileData.phone || "",
          address1: profileData.address_1 || "-",
          address2: profileData.address_2 || "-",
          country: getCountryName(profileData.country_id) || "-",
          state: getStateName(profileData.state_id) || "-",
          city: profileData.city_id || "-",
          zip: profileData.zipcode || "-",
        };

    const businessInfo =
      isEditMode && isBusinessClient
        ? {
            name: renderEditableField(
              "business_name",
              profileData.business_name
            ),
            type: renderEditableField(
              "business_type",
              profileData.business_type,
              {
                type: "select",
                dropdown: [
                  {
                    id: "Sole Proprietorship/DBA",
                    value: "Sole Proprietorship/DBA",
                  },
                  {
                    id: "Limited Liability Company (LLC)",
                    value: "Limited Liability Company (LLC)",
                  },
                  { id: "S Corporation", value: "S Corporation" },
                  { id: "C Corporation", value: "C Corporation" },
                  {
                    id: "Non Profit Organization",
                    value: "Non Profit Organization",
                  },
                ],
              }
            ),
            phone: renderEditableField(
              "business_phone",
              profileData.business_phone,
              { type: "phone", labelname: "" }
            ),
            address1: renderEditableField(
              "business_address_1",
              profileData.business_address_1
            ),
            address2: renderEditableField(
              "business_address_2",
              profileData.business_address_2
            ),
            country: renderEditableField(
              "business_country_id",
              getCountryName(profileData.business_country_id),
              { type: "select", dropdown: master.country || [] }
            ),
            state: renderEditableField(
              "business_state_id",
              getStateName(profileData.business_state_id, true),
              { type: "select", dropdown: master.business_state || [] }
            ),
            city: renderEditableField(
              "business_city_id",
              profileData.business_city_id
            ),
            zip: renderEditableField(
              "business_zipcode",
              profileData.business_zipcode
            ),
          }
        : {
            name: isBusinessClient ? profileData.business_name || "-" : "-",
            type: isBusinessClient ? profileData.business_type || "-" : "-",
            phone: isBusinessClient ? profileData.business_phone || "-" : "-",
            address1: isBusinessClient
              ? profileData.business_address_1 || "-"
              : "-",
            address2: isBusinessClient
              ? profileData.business_address_2 || "-"
              : "-",
            country: isBusinessClient
              ? getCountryName(profileData.business_country_id) || "-"
              : "-",
            state: isBusinessClient
              ? getStateName(profileData.business_state_id, true) || "-"
              : "-",
            city: isBusinessClient ? profileData.business_city_id || "-" : "-",
            zip: isBusinessClient ? profileData.business_zipcode || "-" : "-",
          };

    return [
      {
        title: "Personal",
        heading: [
          "Tax Payer's Name",
          "Email ID",
          "Phone",
          "Address 1",
          "Address 2",
          "City",
          "State",
          "Zip Code",
          "Country",
        ],
        keys: [
          "name",
          "email",
          "phone",
          "address1",
          "address2",
          "city",
          "state",
          "zip",
          "country",
        ],
        info: personalInfo,
      },
      {
        title: "Business",
        heading: [
          "Business Name",
          "Type",
          "Phone",
          "Address 1",
          "Address 2",
          "City",
          "State Incorporated",
          "Zip Code",
          "Country",
        ],
        keys: [
          "name",
          "type",
          "phone",
          "address1",
          "address2",
          "city",
          "state",
          "zip",
          "country",
        ],
        info: businessInfo,
      },
    ];
  }, [
    profileData,
    master.country,
    master.state,
    master.business_state,
    isEditMode,
    editedData,
    isBusinessClient,
  ]);

  const showPersonalSection =
    !showCategoryChooser || categoryTab === "individual";
  const showBusinessSection =
    isBusinessClient && (!showCategoryChooser || categoryTab === "business");

  const showEditProfileButton =
    !isBusinessClient || categoryTab !== "business";

  useEffect(() => {
    if (!showEditProfileButton && isEditMode) {
      setIsEditMode(false);
      setEditedData({});
    }
  }, [showEditProfileButton, isEditMode]);

  const personalSection = profileDetails.find((s) => s.title === "Personal");

  if (isEmployeeOrManager) {
    if (loadingEmployee) {
      return (
        <div className="profile-page-wrapper">
          <p style={{ textAlign: "center", padding: "40px" }}>Loading profile...</p>
        </div>
      );
    }
    if (!employeeData) {
      return (
        <div className="profile-page-wrapper">
          <p style={{ textAlign: "center", padding: "40px" }}>Profile details not found.</p>
        </div>
      );
    }

    const managerName = managerData
      ? `${managerData.first_name || ""} ${managerData.last_name || ""}`.trim()
      : "-";

    let phoneValue = employeeData.phone || "";
    if (phoneValue && !phoneValue.startsWith("+")) {
      phoneValue = `+${phoneValue}`;
    }

    const employeeFields = {
      ...employeeData,
      phone: phoneValue,
      meetingLink: renderMeetingLink(employeeData.meeting_link, {
        emptyText: "—",
      }),
      clientManager: managerName,
      managerName: managerName,
      location: employeeData.branch_office 
    };

    return (
      <div className="profile-page-wrapper">
        <Grid
          container
          spacing={3}
          className="profile-page-content"
          style={{ marginTop: 0 }}
        >
          <Grid item xs={12}>
            <div className="profile-info-card">
              <h3 className="profile-info-card-title">Basic information</h3>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <InfoTable
                    transparent
                    titles={["Employee ID:", "First Name:", "Assigned Work No.:"]}
                    keys={["company_employee_id", "first_name", "phone"]}
                    info={employeeFields}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoTable
                    transparent
                    titles={["Email ID:", "Last Name:", "Meeting link:"]}
                    keys={["email", "last_name", "meetingLink"]}
                    info={employeeFields}
                  />
                </Grid>
              </Grid>
            </div>
          </Grid>
          <Grid item xs={12}>
            <div className="profile-info-card">
              <h3 className="profile-info-card-title">Work information</h3>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <InfoTable
                    transparent
                    titles={["Department:", "Client Manager:"]}
                    keys={["department", "clientManager"]}
                    info={employeeFields}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoTable
                    transparent
                    titles={["Location:", "Manager:"]}
                    keys={["location", "managerName"]}
                    info={employeeFields}
                  />
                </Grid>
              </Grid>
            </div>
          </Grid>
        </Grid>
      </div>
    );
  }

  return (
    <div className="profile-page-wrapper">
      <Grid
        container
        spacing={3}
        className="profile-page-content"
        style={{ marginTop: 0 }}
      >
        {/* Edit Button - hidden on business account details (businesses edited via ClientBusinessManager) */}
        {showEditProfileButton && (
        <Grid item xs={12}>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px"
            }}
          >
            {!isEditMode ? (
              <Button
                text="Edit Profile"
                icon={<EditIcon />}
                handleClick={handleEditClick}
                large
              />
            ) : (
              <>
                <Button
                  text={saving ? "Saving..." : "Save"}
                  icon={<SaveIcon />}
                  handleClick={handleSaveEdit}
                  disable={saving}
                  large
                />
                <Button
                  text="Cancel"
                  icon={<CloseIcon />}
                  handleClick={handleCancelEdit}
                  disable={saving}
                  large
                  ui="transparent"
                  variant="outline"
                />
              </>
            )}
          </div>
        </Grid>
        )}
        {showPersonalSection && personalSection && (
          <Grid item xs={12} sm={12} md={showBusinessSection ? 6 : 12} lg={showBusinessSection ? 6 : 12}>
            <div className="profile-info-card">
              <h3 className="profile-info-card-title">{personalSection.title}</h3>
              <InfoTable
                transparent
                titles={personalSection.heading.map((h) => h + ":")}
                keys={personalSection.keys}
                info={personalSection.info}
              />
            </div>
          </Grid>
        )}
        {showBusinessSection && (
          <Grid item xs={12} sm={12} md={showPersonalSection ? 6 : 12} lg={showPersonalSection ? 6 : 12}>
            <ClientBusinessManager
              clientId={userId}
              clientRecord={profileData}
              canEdit
              allowDelete={false}
              onClientUpdated={(updated) => {
                setProfileData(updated);
                localStorage.setItem("USER_DATA", JSON.stringify(updated));
                syncBusinessAccess(updated);
              }}
            />
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default Profile;
